import React, { ChangeEvent, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ExitPromptType } from '@frame/ui-components/exit-prompt'
import { Footer } from '@frame/ui-components/footer'
import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { H2 } from '@frame/ui-primitives/h2'
import { Input } from '@frame/ui-primitives/input'
import { P } from '@frame/ui-primitives/p'

import { DirectTask, DirectTaskState, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { NO_ARGYLE_ITEM_ID_FOUND_IDENTIFIER } from '@frame/lib/constants'
import { useRootContext } from '@frame/lib/context'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useHeaderTitle } from '@frame/lib/hooks/useHeaderTitle'
import { useSelectItem } from '@frame/lib/hooks/useSelectItem'
import { useShowExitPrompt } from '@frame/lib/hooks/useShowExitPrompt'
import { useStore } from '@frame/lib/stores/useStore'
import { getTextForVerificationType, getVerifierDisplayName } from '@frame/lib/utils/session'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

export function ConfirmEmployer() {
  const task = useDirectTask(true) as DirectTask
  const navigate = useNavigate()
  const location = useLocation()
  const { actions } = useRootContext()
  const { verifierName, verificationType, verifierType, tenantPropertyName } = useStore(({ session }) => ({
    verifierName: session?.originating_party_display_name,
    verificationType: session?.verification_type,
    tenantPropertyName: session?.configuration?.cobranding?.tenant_property?.property_name,
    verifierType: session?.verifier_type,
    docsForMortgageEnabled: session?.configuration.docs_for_mortgage_enabled ?? false,
    tasks: session?.tasks,
  }))
  const showExitPrompt = useShowExitPrompt()
  const { mutateAndNavigate } = useAutoNavigate()
  const [employerName, setEmployerName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { selectItem } = useSelectItem()
  const title = useHeaderTitle()

  const params = new URLSearchParams(location.search)

  const incomeOrEmployment = getTextForVerificationType(verificationType)
  const currentPath = task?.id ? `${routes.confirmEmployer}/${task.id}` : routes.confirmEmployer

  const { fromPlatformSearch, fromDocsForMortgage, item } = location.state ?? {}

  useEffect(() => {
    const paramsOnMount = new URLSearchParams(location.search)
    const query = paramsOnMount.get('q')

    if (query) {
      setEmployerName(query)
    }
  }, [location.search])

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target

    params.set('q', value)
    setEmployerName(value)
    navigate(`${currentPath}?${params.toString()}`, { replace: true, state: location.state })
  }

  async function handleConfirm() {
    setIsLoading(true)
    if (fromPlatformSearch) {
      selectItem({ employerName, item })
      return
    }

    if (fromDocsForMortgage) {
      const preparedTask = await actions.addStrategyToDirectTask({
        strategy: TrueworkJsStrategy.CREDENTIALS,
        directTaskId: task.id,
      })
      await actions.skipDirectTask(task.id)
      if (!preparedTask) {
        throw new Error('Failed to add credentials strategy to task')
      }
      mutateAndNavigate()
      return
    }

    if (task?.id) {
      if (task.state === DirectTaskState.SKIPPED) {
        await actions.unSkipDirectTask(task.id)
      }

      await actions.updateDirectTask(task.id, {
        employer_name: employerName,
        connector_company_identifier: NO_ARGYLE_ITEM_ID_FOUND_IDENTIFIER,
      })
    } else {
      await actions.createDirectTask({
        employer_name: employerName,
        connector_company_identifier: NO_ARGYLE_ITEM_ID_FOUND_IDENTIFIER,
      })
    }

    mutateAndNavigate()
  }

  const displayName = getVerifierDisplayName(verifierType, verifierName, tenantPropertyName)
  const trueworkWithEmployer = `Truework or ${displayName}`
  const unableToVerify = !fromPlatformSearch
  const buttonText = unableToVerify ? 'Submit' : 'Continue'
  return (
    <>
      <WidgetHeaderWithNavigation
        title={title}
        showExitPrompt={showExitPrompt}
        exitPromptType={ExitPromptType.EMPLOYER}
      />
      <Body data-cy={DataAttribute.UNIFIED_SEARCH_CONFIRM_EMPLOYER} className="p-5" paddingTopOverride={8}>
        <H2 className="text mb-4 text-left text-moss-950">Confirm your employer</H2>
        {unableToVerify && (
          <P className="mb-5">
            {trueworkWithEmployer} will attempt to verify your {incomeOrEmployment} another way.
          </P>
        )}
        <Input
          data-dd-privacy="allow"
          autoFocus
          label="Employer"
          value={employerName}
          onChange={handleInputChange}
          tagType={null}
          disabled={isLoading}
        />
      </Body>
      <Footer
        buttons={{
          primary: (
            <Button className="mt-5" disabled={isLoading || !employerName} onClick={handleConfirm} applyCobranding>
              {isLoading ? <LoadingSpinner /> : buttonText}
            </Button>
          ),
        }}
      />
    </>
  )
}
